import React, {useState, useCallback, useRef, useEffect} from "react"
import {useIoTShadowStore, ShadowType} from "../../store/iotShadowStore"
import {useIsReadOnly} from "../../store/authStore"

export type SectionType = "desired" | "reported" | "delta" | "other"

export const SECTION_STYLES: Record<
	SectionType,
	{
		bg: string
		border: string
		label: string
		labelColor: string
		keyColor: string
		valueColor: string
		highlightBg: string
	}
> = {
	desired: {
		bg: "bg-blue-950/40 dark:bg-blue-950/60",
		border: "border-l-4 border-blue-500",
		label: "DESIRED",
		labelColor: "text-blue-400 font-bold text-xs tracking-widest",
		keyColor: "text-blue-300",
		valueColor: "text-blue-100",
		highlightBg: "bg-blue-500/25",
	},
	reported: {
		bg: "bg-emerald-950/40 dark:bg-emerald-950/60",
		border: "border-l-4 border-emerald-500",
		label: "REPORTED",
		labelColor: "text-emerald-400 font-bold text-xs tracking-widest",
		keyColor: "text-emerald-300",
		valueColor: "text-emerald-100",
		highlightBg: "bg-emerald-500/25",
	},
	delta: {
		bg: "bg-amber-950/40 dark:bg-amber-950/60",
		border: "border-l-4 border-amber-500",
		label: "DELTA",
		labelColor: "text-amber-400 font-bold text-xs tracking-widest",
		keyColor: "text-amber-300",
		valueColor: "text-amber-100",
		highlightBg: "bg-amber-500/25",
	},
	other: {
		bg: "bg-gray-900/40",
		border: "border-l-4 border-gray-600",
		label: "",
		labelColor: "text-gray-400",
		keyColor: "text-gray-300",
		valueColor: "text-gray-100",
		highlightBg: "bg-gray-500/25",
	},
}

export const detectSection = (path: string): SectionType => {
	const parts = path.split(".")
	const stateIdx = parts.indexOf("state")
	if (stateIdx !== -1 && parts[stateIdx + 1]) {
		const sub = parts[stateIdx + 1]
		if (sub === "desired") return "desired"
		if (sub === "reported") return "reported"
		if (sub === "delta") return "delta"
	}
	return "other"
}

const getSectionPrefix = (sectionType: SectionType): string => {
	if (sectionType === "desired") return "state.desired."
	if (sectionType === "reported") return "state.reported."
	return ""
}

const getLeafPath = (path: string, sectionType: SectionType): string => {
	const prefix = getSectionPrefix(sectionType)
	if (prefix && path.startsWith(prefix)) return path.slice(prefix.length)
	return path
}

type PendingChanges = Record<string, unknown>

const fmtTs = (): string => new Date().toISOString().slice(11, 23)

const InlineValueEditor = ({
	value,
	path,
	onCommit,
	onCancel,
}: {
	value: unknown
	path: string
	onCommit: (path: string, newValue: unknown) => void
	onCancel: () => void
}) => {
	const inputRef = useRef<HTMLInputElement>(null)
	const [draft, setDraft] = useState<string>(
		typeof value === "object" && value !== null ? JSON.stringify(value) : String(value ?? ""),
	)

	useEffect(() => {
		inputRef.current?.focus()
		inputRef.current?.select()
	}, [])

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault()
			commit()
		} else if (e.key === "Escape") {
			onCancel()
		}
	}

	const parseTyped = (raw: string): unknown => {
		const trimmed = raw.trim()
		if (trimmed === "true") return true
		if (trimmed === "false") return false
		if (trimmed === "null") return null
		const asNum = Number(trimmed)
		if (!isNaN(asNum) && trimmed !== "") return asNum
		try {
			return JSON.parse(trimmed)
		} catch {
			return trimmed
		}
	}

	const commit = () => {
		onCommit(path, parseTyped(draft))
	}

	const isBoolean = typeof value === "boolean"

	if (isBoolean) {
		return (
			<button
				className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono border border-blue-400/60 bg-blue-900/40 text-purple-300 hover:bg-blue-800/60 transition-colors"
				onClick={() => onCommit(path, !value)}>
				<span className="text-gray-500">toggle →</span>
				<span className="text-purple-200">{String(!value)}</span>
			</button>
		)
	}

	return (
		<span className="inline-flex items-center gap-1">
			<input
				ref={inputRef}
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onKeyDown={handleKeyDown}
				onBlur={commit}
				className="font-mono text-xs bg-blue-900/50 border border-blue-400/70 rounded px-1.5 py-0.5 text-blue-100 outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-400/40 min-w-[60px]"
				style={{width: `${Math.max(60, draft.length * 7.5 + 20)}px`}}
				spellCheck={false}
			/>
			<span className="text-gray-600 text-[10px] select-none">↵ commit · esc cancel</span>
		</span>
	)
}

const NewKeyValueRow = ({
	indent,
	sectionType,
	onCommit,
	onCancel,
}: {
	indent: number
	sectionType: SectionType
	onCommit: (key: string, value: unknown) => void
	onCancel: () => void
}) => {
	const keyRef = useRef<HTMLInputElement>(null)
	const valRef = useRef<HTMLInputElement>(null)
	const [key, setKey] = useState("")
	const [val, setVal] = useState("")
	const styles = SECTION_STYLES[sectionType]
	const indentStr = "  ".repeat(indent)

	useEffect(() => {
		keyRef.current?.focus()
	}, [])

	const parseTyped = (raw: string): unknown => {
		const trimmed = raw.trim()
		if (trimmed === "true") return true
		if (trimmed === "false") return false
		if (trimmed === "null") return null
		const asNum = Number(trimmed)
		if (!isNaN(asNum) && trimmed !== "") return asNum
		try {
			return JSON.parse(trimmed)
		} catch {
			return trimmed
		}
	}

	const commit = () => {
		if (key.trim()) onCommit(key.trim(), parseTyped(val))
		else onCancel()
	}

	const handleKeyKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Tab") {
			e.preventDefault()
			valRef.current?.focus()
		} else if (e.key === "Escape") onCancel()
		else if (e.key === "Enter") {
			e.preventDefault()
			valRef.current?.focus()
		}
	}

	const handleValKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault()
			commit()
		} else if (e.key === "Escape") onCancel()
	}

	return (
		<span className="inline-flex items-center gap-1 w-full">
			{indentStr}
			{"  "}
			<span className="text-gray-500 text-[10px] select-none mr-0.5">new key:</span>
			<input
				ref={keyRef}
				value={key}
				onChange={(e) => setKey(e.target.value)}
				onKeyDown={handleKeyKeyDown}
				placeholder="KEY"
				className={`font-mono text-xs bg-transparent border border-dashed rounded px-1.5 py-0.5 outline-none focus:border-blue-400 min-w-[60px] ${styles.keyColor} border-blue-500/40 placeholder-blue-800`}
				style={{width: `${Math.max(60, key.length * 7.5 + 24)}px`}}
				spellCheck={false}
			/>
			<span className="text-gray-400">: </span>
			<input
				ref={valRef}
				value={val}
				onChange={(e) => setVal(e.target.value)}
				onKeyDown={handleValKeyDown}
				onBlur={commit}
				placeholder="value"
				className="font-mono text-xs bg-blue-900/30 border border-blue-400/50 rounded px-1.5 py-0.5 text-blue-100 outline-none focus:border-blue-300 min-w-[60px] placeholder-blue-900"
				style={{width: `${Math.max(60, val.length * 7.5 + 24)}px`}}
				spellCheck={false}
			/>
			<span className="text-gray-600 text-[10px] select-none">tab · ↵ · esc</span>
		</span>
	)
}

const PendingBadge = ({value}: {value: unknown}) => (
	<span className="inline-flex items-center gap-1 ml-1">
		<span className="text-[9px] text-amber-400/80 font-mono uppercase tracking-wider">pending</span>
		<span className="text-amber-300 font-mono text-xs">{renderPrimitiveValue(value)}</span>
	</span>
)

const renderPrimitiveValue = (value: unknown): React.ReactNode => {
	if (value === null) return <span className="text-gray-400">null</span>
	if (typeof value === "boolean") return <span className="text-purple-300">{String(value)}</span>
	if (typeof value === "number") return <span className="text-orange-300">{value}</span>
	if (typeof value === "string") return <span className="text-green-300">"{value}"</span>
	return <span className="text-gray-300">{String(value)}</span>
}

const formatTs = (ts: number): string => {
	try {
		return new Date(ts * 1000).toISOString().replace("T", " ").slice(0, 19) + " UTC"
	} catch {
		return String(ts)
	}
}

const extractTimestamp = (val: unknown): number | null => {
	if (typeof val === "number") return val
	if (typeof val === "object" && val !== null) {
		const obj = val as Record<string, unknown>
		if (typeof obj.timestamp === "number") return obj.timestamp
	}
	return null
}

const flattenMetadataToRows = (
	data: Record<string, unknown>,
	prefix = "",
): Array<{key: string; timestamp: number | null; raw: unknown}> => {
	const rows: Array<{key: string; timestamp: number | null; raw: unknown}> = []

	for (const [k, v] of Object.entries(data)) {
		const fullKey = prefix ? `${prefix}.${k}` : k
		const ts = extractTimestamp(v)

		if (ts !== null) {
			rows.push({key: fullKey, timestamp: ts, raw: v})
		} else if (typeof v === "object" && v !== null && !Array.isArray(v)) {
			const nested = flattenMetadataToRows(v as Record<string, unknown>, fullKey)
			if (nested.length > 0) {
				rows.push(...nested)
			} else {
				rows.push({key: fullKey, timestamp: null, raw: v})
			}
		} else {
			rows.push({key: fullKey, timestamp: null, raw: v})
		}
	}

	return rows
}

const MetadataTable = ({
	data,
	sectionType,
}: {
	data: Record<string, unknown>
	sectionType: SectionType
}) => {
	const styles = SECTION_STYLES[sectionType]

	const topLevelSections: Array<{
		sectionKey: string
		rows: Array<{key: string; timestamp: number | null; raw: unknown}>
	}> = []

	for (const [topKey, topVal] of Object.entries(data)) {
		const ts = extractTimestamp(topVal)

		if (ts !== null) {
			const existing = topLevelSections.find((s) => s.sectionKey === "__root__")
			if (existing) existing.rows.push({key: topKey, timestamp: ts, raw: topVal})
			else
				topLevelSections.push({
					sectionKey: "__root__",
					rows: [{key: topKey, timestamp: ts, raw: topVal}],
				})
		} else if (typeof topVal === "object" && topVal !== null && !Array.isArray(topVal)) {
			const rows = flattenMetadataToRows(topVal as Record<string, unknown>)
			topLevelSections.push({sectionKey: topKey, rows})
		} else {
			const existing = topLevelSections.find((s) => s.sectionKey === "__root__")
			if (existing) existing.rows.push({key: topKey, timestamp: null, raw: topVal})
			else
				topLevelSections.push({
					sectionKey: "__root__",
					rows: [{key: topKey, timestamp: null, raw: topVal}],
				})
		}
	}

	return (
		<div className="space-y-3">
			{topLevelSections.map(({sectionKey, rows}) => (
				<div key={sectionKey} className="rounded overflow-hidden border border-gray-700/50">
					{sectionKey !== "__root__" && (
						<div className="px-3 py-1 bg-gray-800/60 border-b border-gray-700/50">
							<span
								className={`text-[10px] font-semibold tracking-widest uppercase ${styles.keyColor}`}>
								{sectionKey}
							</span>
						</div>
					)}
					<table className="w-full text-xs font-mono">
						<tbody>
							{rows.map(({key, timestamp, raw}, idx) => (
								<tr
									key={key}
									className={`${idx % 2 === 0 ? "bg-gray-900/30" : "bg-gray-900/10"} border-b border-gray-700/20 last:border-0`}>
									<td className={`px-3 py-1.5 w-1/2 ${styles.keyColor} select-text`}>"{key}"</td>
									<td className="px-3 py-1.5 text-right">
										{timestamp !== null ? (
											<span className="inline-flex items-center gap-2">
												<span className="text-orange-300">{timestamp}</span>
												<span className="text-gray-500 text-[10px]">{formatTs(timestamp)}</span>
											</span>
										) : (
											<span className="text-gray-400 text-[10px] italic">
												{JSON.stringify(raw)}
											</span>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			))}
		</div>
	)
}

export const JsonNode = ({
	value,
	path,
	indent,
	selectedPairId,
	onSelectPair,
	editingPath,
	onStartEdit,
	onCommitEdit,
	onCancelEdit,
	pendingChanges,
	isEditable,
	sectionType,
	onAddNewKey,
	addingKeyAtPath,
	onCommitNewKey,
	onCancelNewKey,
	originalData,
}: {
	value: unknown
	path: string
	indent: number
	selectedPairId: string | null
	onSelectPair: (id: string | null) => void
	editingPath: string | null
	onStartEdit: (path: string) => void
	onCommitEdit: (path: string, value: unknown) => void
	onCancelEdit: () => void
	pendingChanges: PendingChanges
	isEditable: boolean
	sectionType: SectionType
	onAddNewKey: (path: string) => void
	addingKeyAtPath: string | null
	onCommitNewKey: (parentPath: string, key: string, value: unknown) => void
	onCancelNewKey: () => void
	originalData: unknown
}) => {
	const styles = SECTION_STYLES[sectionType]
	const indentStr = "  ".repeat(indent)

	const leafPath = getLeafPath(path, sectionType)
	const hasPending = leafPath in pendingChanges
	const isEditingThis = editingPath === path

	const childProps = {
		editingPath,
		onStartEdit,
		onCommitEdit,
		onCancelEdit,
		pendingChanges,
		isEditable,
		selectedPairId,
		onSelectPair,
		sectionType,
		onAddNewKey,
		addingKeyAtPath,
		onCommitNewKey,
		onCancelNewKey,
		originalData,
	}

	if (
		value === null ||
		typeof value === "boolean" ||
		typeof value === "number" ||
		typeof value === "string"
	) {
		if (isEditingThis && isEditable) {
			return (
				<InlineValueEditor
					value={hasPending ? pendingChanges[leafPath] : value}
					path={path}
					onCommit={onCommitEdit}
					onCancel={onCancelEdit}
				/>
			)
		}

		const displayValue = hasPending ? pendingChanges[leafPath] : value
		const originalValue = value

		return (
			<span
				className={`inline-flex items-center gap-1 ${isEditable ? "cursor-text" : ""}`}
				onDoubleClick={
					isEditable
						? (e) => {
								e.stopPropagation()
								onStartEdit(path)
							}
						: undefined
				}>
				{hasPending ? (
					<span className="relative">
						<span className="line-through opacity-30 text-gray-400 text-[10px]">
							{renderPrimitiveValue(originalValue)}
						</span>
						<PendingBadge value={displayValue} />
					</span>
				) : (
					<span>{renderPrimitiveValue(displayValue)}</span>
				)}
			</span>
		)
	}

	if (Array.isArray(value)) {
		if (value.length === 0) {
			if (isEditingThis && isEditable) {
				return (
					<InlineValueEditor
						value={hasPending ? pendingChanges[leafPath] : "[]"}
						path={path}
						onCommit={onCommitEdit}
						onCancel={onCancelEdit}
					/>
				)
			}
			return (
				<span
					className={`text-gray-400 ${isEditable ? "cursor-text" : ""}`}
					onDoubleClick={
						isEditable
							? (e) => {
									e.stopPropagation()
									onStartEdit(path)
								}
							: undefined
					}>
					[]
				</span>
			)
		}
		return (
			<span>
				<span className="text-gray-400">{"["}</span>
				{"\n"}
				{value.map((item, i) => {
					const itemPath = `${path}[${i}]`
					const pairId = itemPath
					const isSelected = selectedPairId === pairId
					return (
						<span key={i}>
							<span
								className={`cursor-pointer rounded transition-colors duration-100 ${isSelected ? styles.highlightBg : "hover:bg-white/5"}`}
								onClick={(e) => {
									e.stopPropagation()
									onSelectPair(isSelected ? null : pairId)
								}}>
								{indentStr}
								{"  "}
								<JsonNode value={item} path={itemPath} indent={indent + 1} {...childProps} />
								{i < value.length - 1 && <span className="text-gray-400">,</span>}
							</span>
							{"\n"}
						</span>
					)
				})}
				{indentStr}
				<span className="text-gray-400">{"]"}</span>
			</span>
		)
	}

	if (typeof value === "object") {
		const entries = Object.entries(value as Record<string, unknown>)
		const isAddingHere = addingKeyAtPath === path
		if (entries.length === 0 && !isAddingHere) return <span className="text-gray-400">{"{}"}</span>

		return (
			<span>
				<span className="text-gray-400">{"{"}</span>
				{"\n"}
				{entries.map(([k, v], i) => {
					const childPath = path ? `${path}.${k}` : k
					const pairId = childPath
					const isSelected = selectedPairId === pairId
					const childLeafPath = getLeafPath(childPath, sectionType)
					const childHasPending = childLeafPath in pendingChanges

					return (
						<span key={k}>
							<span
								className={`cursor-pointer rounded transition-colors duration-100 ${
									childHasPending
										? "bg-amber-500/10 border-l-2 border-amber-400/50"
										: isSelected
											? styles.highlightBg
											: "hover:bg-white/5"
								}`}
								onClick={(e) => {
									e.stopPropagation()
									onSelectPair(isSelected ? null : pairId)
								}}>
								{indentStr}
								{"  "}
								<span className={styles.keyColor}>"{k}"</span>
								<span className="text-gray-400">: </span>
								<span>
									<JsonNode value={v} path={childPath} indent={indent + 1} {...childProps} />
									{(i < entries.length - 1 || isAddingHere) && (
										<span className="text-gray-400">,</span>
									)}
								</span>
							</span>
							{"\n"}
						</span>
					)
				})}
				{isAddingHere && (
					<span className="inline-block w-full">
						<NewKeyValueRow
							indent={indent}
							sectionType={sectionType}
							onCommit={(key, val) => onCommitNewKey(path, key, val)}
							onCancel={onCancelNewKey}
						/>
						{"\n"}
					</span>
				)}
				{indentStr}
				<span className="text-gray-400">{"}"}</span>
			</span>
		)
	}

	return <span className="text-gray-300">{String(value)}</span>
}

const ReportedWarningBanner = ({onDismiss}: {onDismiss: () => void}) => (
	<div className="flex items-start gap-2 px-3 py-2 bg-amber-950/60 border-b border-amber-500/40 text-xs text-amber-300">
		<span className="mt-0.5 shrink-0 text-amber-400">⚠</span>
		<span className="flex-1">
			<span className="font-semibold text-amber-200">Caution: </span>
			This section reflects values reported directly by the device. Editing these fields will write
			to the shadow's <span className="font-mono text-amber-200">desired</span> state but the device
			may overwrite them on its next sync. Only modify if you know what you're doing.
		</span>
		<button
			onClick={onDismiss}
			className="shrink-0 text-amber-500 hover:text-amber-300 transition-colors leading-none mt-0.5">
			✕
		</button>
	</div>
)

const SaveLog = ({logs}: {logs: string[]}) => {
	const bottomRef = useRef<HTMLDivElement>(null)
	const [collapsed, setCollapsed] = useState(false)

	useEffect(() => {
		if (!collapsed) {
			bottomRef.current?.scrollIntoView({behavior: "smooth", block: "nearest"})
		}
	}, [logs, collapsed])

	if (logs.length === 0) return null

	return (
		<div className="mx-3 mb-2 rounded border border-gray-700/60 bg-gray-950/80 overflow-hidden">
			<div className="px-2 py-1 border-b border-gray-700/40 flex items-center gap-1.5">
				<span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
				<span className="text-[10px] text-gray-500 font-mono uppercase tracking-wider">
					save log
				</span>
				<button
					onClick={() => setCollapsed((v) => !v)}
					className="ml-auto text-gray-600 hover:text-gray-300 transition-colors leading-none text-xs mr-2">
					✕
				</button>
			</div>
			{!collapsed && (
				<div className="max-h-24 overflow-y-auto p-2 space-y-0.5">
					{logs.map((line, i) => {
						const isError = line.includes("[ERROR]")
						const isOk = line.includes("[OK]")
						return (
							<div
								key={i}
								className={`text-[10px] font-mono leading-4 ${
									isError ? "text-red-400" : isOk ? "text-emerald-400" : "text-gray-400"
								}`}>
								{line}
							</div>
						)
					})}
					<div ref={bottomRef} />
				</div>
			)}
		</div>
	)
}
export const SectionBlock = ({
	label,
	sectionType,
	data,
	path,
	selectedPairId,
	onSelectPair,
	editingPath,
	onStartEdit,
	onCommitEdit,
	onCancelEdit,
	pendingChanges,
	onSavePending,
	onDiscardPending,
	isSaving,
	saveLogs,
	onAddNewKey,
	addingKeyAtPath,
	onCommitNewKey,
	onCancelNewKey,
	isMetadata,
	readOnly = false,
}: {
	label: string
	sectionType: SectionType
	data: unknown
	path: string
	selectedPairId: string | null
	onSelectPair: (id: string | null) => void
	editingPath: string | null
	onStartEdit: (path: string) => void
	onCommitEdit: (path: string, value: unknown) => void
	onCancelEdit: () => void
	pendingChanges: PendingChanges
	onSavePending?: () => void
	onDiscardPending?: () => void
	isSaving?: boolean
	saveLogs?: string[]
	onAddNewKey: (path: string) => void
	addingKeyAtPath: string | null
	onCommitNewKey: (parentPath: string, key: string, value: unknown) => void
	onCancelNewKey: () => void
	isMetadata?: boolean
	/** Read-only roles (viewer / viewer_all): no inline edit, no add field, no save. */
	readOnly?: boolean
}) => {
	const styles = SECTION_STYLES[sectionType]
	const isEditable = !readOnly && (sectionType === "desired" || sectionType === "reported")
	const pendingCount = Object.keys(pendingChanges).length
	const hasPending = isEditable && pendingCount > 0
	const [warningDismissed, setWarningDismissed] = useState(false)
	const [showReportedWarning, setShowReportedWarning] = useState(false)

	const handleStartEditWithWarning = useCallback(
		(p: string) => {
			if (sectionType === "reported" && !warningDismissed) {
				setShowReportedWarning(true)
			}
			onStartEdit(p)
		},
		[sectionType, warningDismissed, onStartEdit],
	)

	const handleDismissWarning = () => {
		setWarningDismissed(true)
		setShowReportedWarning(false)
	}

	const rootPath = path

	const handleClickEnd = (e: React.MouseEvent) => {
		if (!isEditable) return
		const target = e.target as HTMLElement
		const pre = target.closest("pre")
		if (!pre) return
		if (target === pre || target.tagName === "PRE") {
			onAddNewKey(rootPath)
		}
	}

	return (
		<div className={`rounded-md mb-2 overflow-hidden ${styles.border}`}>
			{showReportedWarning && sectionType === "reported" && (
				<ReportedWarningBanner onDismiss={handleDismissWarning} />
			)}
			<div className={`px-3 py-1.5 flex items-center gap-2 ${styles.bg}`}>
				<span className={styles.labelColor}>{label}</span>
				{isEditable && (
					<span className="text-[10px] text-gray-500 ml-auto select-none">
						double-click value to edit · click end of section to add field
					</span>
				)}
			</div>
			<div className={`p-3 ${styles.bg}`} onClick={handleClickEnd}>
				{isMetadata && typeof data === "object" && data !== null && !Array.isArray(data) ? (
					<MetadataTable data={data as Record<string, unknown>} sectionType={sectionType} />
				) : (
					<pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto leading-5">
						<JsonNode
							value={data}
							path={path}
							indent={0}
							selectedPairId={selectedPairId}
							onSelectPair={onSelectPair}
							editingPath={editingPath}
							onStartEdit={handleStartEditWithWarning}
							onCommitEdit={onCommitEdit}
							onCancelEdit={onCancelEdit}
							pendingChanges={pendingChanges}
							isEditable={isEditable}
							sectionType={sectionType}
							onAddNewKey={onAddNewKey}
							addingKeyAtPath={addingKeyAtPath}
							onCommitNewKey={onCommitNewKey}
							onCancelNewKey={onCancelNewKey}
							originalData={data}
						/>
					</pre>
				)}
			</div>

			<SaveLog logs={saveLogs ?? []} />

			{hasPending && (
				<div className="flex items-center gap-2 px-3 py-2 bg-gray-900/95 backdrop-blur border-t border-amber-500/30 w-full">
					<button
						onClick={onDiscardPending}
						disabled={isSaving}
						className="shrink-0 text-xs px-2.5 py-1 rounded border border-gray-600 text-gray-400 hover:text-gray-200 hover:border-gray-400 transition-colors disabled:opacity-40">
						Discard
					</button>
					<button
						onClick={onSavePending}
						disabled={isSaving}
						className="shrink-0 text-xs px-3 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5">
						{isSaving ? (
							<>
								<span className="inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
								Saving…
							</>
						) : (
							<>Save changes</>
						)}
					</button>
					<span className="text-xs text-amber-400/90 flex items-center gap-1.5 ml-1 min-w-0">
						<span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
						<span className="truncate">
							{pendingCount} unsaved {pendingCount === 1 ? "change" : "changes"}
						</span>
					</span>
				</div>
			)}
		</div>
	)
}

export interface ColoredJsonViewerProps {
	data: unknown
	shadowType: "default" | "radar"
	deviceId?: string
}

interface ShadowLike {
	state?: {
		desired?: unknown
		reported?: unknown
		delta?: unknown
		[key: string]: unknown
	}
	metadata?: Record<string, unknown>
}

export const ColoredJsonViewer = ({data, shadowType, deviceId}: ColoredJsonViewerProps) => {
	const updateDeviceShadow = useIoTShadowStore((state) => state.updateDeviceShadow)
	const isReadOnly = useIsReadOnly()

	const [selectedPairId, setSelectedPairId] = useState<string | null>(null)
	const [editingPath, setEditingPath] = useState<string | null>(null)
	const [pendingBySection, setPendingBySection] = useState<Record<SectionType, PendingChanges>>({
		desired: {},
		reported: {},
		delta: {},
		other: {},
	})
	const [savingSection, setSavingSection] = useState<SectionType | null>(null)
	const [addingKeyAtPath, setAddingKeyAtPath] = useState<string | null>(null)
	const [logsBySection, setLogsBySection] = useState<Record<SectionType, string[]>>({
		desired: [],
		reported: [],
		delta: [],
		other: [],
	})

	const undoHistory = useRef<Array<{section: SectionType; snapshot: PendingChanges}>>([])

	const shadowData = data as ShadowLike
	const originalSectionData = useRef<Record<SectionType, unknown>>({
		desired: null,
		reported: null,
		delta: null,
		other: null,
	})

	useEffect(() => {
		originalSectionData.current = {
			desired: shadowData?.state?.desired,
			reported: shadowData?.state?.reported,
			delta: shadowData?.state?.delta,
			other: null,
		}
	}, [data])

	const appendLog = useCallback((sectionType: SectionType, line: string) => {
		setLogsBySection((prev) => ({
			...prev,
			[sectionType]: [...prev[sectionType], `[${fmtTs()}] ${line}`],
		}))
	}, [])

	const getActiveSectionWithPending = useCallback((): SectionType | null => {
		const order: SectionType[] = ["desired", "reported"]
		for (const s of order) {
			if (Object.keys(pendingBySection[s]).length > 0) return s
		}
		return null
	}, [pendingBySection])

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (editingPath !== null) return

			if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
				const section = getActiveSectionWithPending()
				if (!section) return
				e.preventDefault()
				handleSave(section)
				return
			}

			if (e.key === "Escape") {
				const section = getActiveSectionWithPending()
				if (!section) return
				e.preventDefault()
				handleDiscard(section)
				return
			}

			if ((e.ctrlKey || e.metaKey) && e.key === "z") {
				e.preventDefault()
				const last = undoHistory.current[undoHistory.current.length - 1]
				if (!last) return
				undoHistory.current = undoHistory.current.slice(0, -1)
				setPendingBySection((prev) => ({...prev, [last.section]: last.snapshot}))
				appendLog(last.section, "Undo — reverted last change.")
				return
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [editingPath, getActiveSectionWithPending])

	const handleSelectPair = useCallback((id: string | null) => setSelectedPairId(id), [])

	const handleStartEdit = useCallback((path: string) => {
		setEditingPath(path)
		setAddingKeyAtPath(null)
	}, [])

	const handleCommitEdit = useCallback((editPath: string, newValue: unknown) => {
		const section = detectSection(editPath)
		const leafPath = getLeafPath(editPath, section)

		const keys = leafPath.split(".")
		let cur: unknown = originalSectionData.current[section]
		for (const k of keys) {
			if (cur == null || typeof cur !== "object") {
				cur = undefined
				break
			}
			cur = (cur as Record<string, unknown>)[k]
		}
		const originalValue = cur

		setPendingBySection((prev) => {
			undoHistory.current = [...undoHistory.current, {section, snapshot: {...prev[section]}}]

			if (newValue === originalValue) {
				const next = {...prev[section]}
				delete next[leafPath]
				return {...prev, [section]: next}
			}
			return {...prev, [section]: {...prev[section], [leafPath]: newValue}}
		})
		setEditingPath(null)
	}, [])

	const handleCancelEdit = useCallback(() => setEditingPath(null), [])

	const handleAddNewKey = useCallback((path: string) => {
		setAddingKeyAtPath(path)
		setEditingPath(null)
	}, [])

	const handleCommitNewKey = useCallback((parentPath: string, key: string, value: unknown) => {
		const actualSection: SectionType = parentPath.includes("desired")
			? "desired"
			: parentPath.includes("reported")
				? "reported"
				: "desired"
		setPendingBySection((prev) => {
			undoHistory.current = [
				...undoHistory.current,
				{section: actualSection, snapshot: {...prev[actualSection]}},
			]
			return {...prev, [actualSection]: {...prev[actualSection], [key]: value}}
		})
		setAddingKeyAtPath(null)
	}, [])

	const handleCancelNewKey = useCallback(() => setAddingKeyAtPath(null), [])

	const handleSave = useCallback(
		async (sectionType: SectionType) => {
			const changes = pendingBySection[sectionType]
			const keys = Object.keys(changes)
			if (keys.length === 0 || isReadOnly) return

			appendLog(
				sectionType,
				`Saving ${keys.length} change${keys.length !== 1 ? "s" : ""}: ${keys.join(", ")}`,
			)

			if (!deviceId) {
				appendLog(sectionType, "[ERROR] No deviceId provided — cannot persist changes.")
				return
			}

			const storeShadowType: ShadowType = shadowType === "default" ? "classic" : "radar"

			setSavingSection(sectionType)
			try {
				await updateDeviceShadow(deviceId, storeShadowType, changes)
				appendLog(sectionType, "[OK] Changes saved successfully.")
				setPendingBySection((prev) => ({...prev, [sectionType]: {}}))
				undoHistory.current = undoHistory.current.filter((h) => h.section !== sectionType)
			} catch (err) {
				const msg = err instanceof Error ? err.message : String(err)
				appendLog(sectionType, `[ERROR] Save failed: ${msg}`)
			} finally {
				setSavingSection(null)
			}
		},
		[deviceId, shadowType, updateDeviceShadow, pendingBySection, appendLog, isReadOnly],
	)

	const handleDiscard = useCallback(
		(sectionType: SectionType) => {
			const count = Object.keys(pendingBySection[sectionType]).length
			appendLog(sectionType, `Discarded ${count} pending change${count !== 1 ? "s" : ""}.`)
			setPendingBySection((prev) => ({...prev, [sectionType]: {}}))
			undoHistory.current = undoHistory.current.filter((h) => h.section !== sectionType)
			setEditingPath(null)
			setAddingKeyAtPath(null)
		},
		[pendingBySection, appendLog],
	)

	const state = shadowData?.state
	const hasKnownSections =
		state?.desired !== undefined || state?.reported !== undefined || state?.delta !== undefined

	const sharedProps = {
		selectedPairId,
		onSelectPair: handleSelectPair,
		editingPath,
		onStartEdit: handleStartEdit,
		onCommitEdit: handleCommitEdit,
		onCancelEdit: handleCancelEdit,
		addingKeyAtPath,
		onAddNewKey: handleAddNewKey,
		onCommitNewKey: handleCommitNewKey,
		onCancelNewKey: handleCancelNewKey,
		readOnly: isReadOnly,
	}

	return (
		<div onClick={() => setSelectedPairId(null)}>
			{state?.desired !== undefined && (
				<SectionBlock
					label="DESIRED"
					sectionType="desired"
					data={state.desired}
					path="state.desired"
					{...sharedProps}
					pendingChanges={pendingBySection.desired}
					isSaving={savingSection === "desired"}
					saveLogs={logsBySection.desired}
				/>
			)}
			{state?.reported !== undefined && (
				<SectionBlock
					label="REPORTED"
					sectionType="reported"
					data={state.reported}
					path="state.reported"
					{...sharedProps}
					pendingChanges={pendingBySection.reported}
					isSaving={savingSection === "reported"}
					saveLogs={logsBySection.reported}
				/>
			)}
			{state?.delta !== undefined && (
				<SectionBlock
					label="DELTA"
					sectionType="delta"
					data={state.delta}
					path="state.delta"
					{...sharedProps}
					pendingChanges={pendingBySection.delta}
					saveLogs={logsBySection.delta}
				/>
			)}
			{shadowData?.metadata !== undefined && (
				<SectionBlock
					label="METADATA"
					sectionType="other"
					data={shadowData.metadata}
					path="metadata"
					{...sharedProps}
					pendingChanges={pendingBySection.other}
					saveLogs={logsBySection.other}
					isMetadata={true}
				/>
			)}
			{!hasKnownSections && (
				<SectionBlock
					label="DATA"
					sectionType="other"
					data={shadowData}
					path=""
					{...sharedProps}
					pendingChanges={pendingBySection.other}
					saveLogs={logsBySection.other}
				/>
			)}
		</div>
	)
}

export const ShadowLegend = ({readOnly = false}: {readOnly?: boolean}) => (
	<div className="flex gap-2 mb-3 flex-wrap">
		<span className="flex items-center gap-1.5 text-xs text-blue-400">
			<span className="inline-block w-3 h-3 rounded-sm bg-blue-500/60 border border-blue-500" />
			Desired
		</span>
		<span className="flex items-center gap-1.5 text-xs text-emerald-400">
			<span className="inline-block w-3 h-3 rounded-sm bg-emerald-500/60 border border-emerald-500" />
			Reported
		</span>
		<span className="flex items-center gap-1.5 text-xs text-amber-400">
			<span className="inline-block w-3 h-3 rounded-sm bg-amber-500/60 border border-amber-500" />
			Delta
		</span>
		<span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
			{readOnly
				? "· Click to highlight · Read-only access"
				: "· Click to highlight · Double-click value to edit · Click below last field to add"}
		</span>
	</div>
)

export const ShadowPanel = ({
	data,
	shadowType,
	deviceId,
}: {
	data: unknown
	shadowType: "default" | "radar"
	deviceId?: string
}) => {
	const isReadOnly = useIsReadOnly()
	if (!data) {
		return (
			<div className="text-gray-500 dark:text-gray-400 italic text-center py-4">
				No shadow data available
			</div>
		)
	}

	return (
		<div>
			<ShadowLegend readOnly={isReadOnly} />
			<div className="rounded-lg overflow-hidden border border-gray-700">
				<ColoredJsonViewer data={data} shadowType={shadowType} deviceId={deviceId} />
			</div>
		</div>
	)
}
